/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppVersionTableCellProps } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionTableCellProps';

const messages = defineMessages({
    activeVersionSubtitle: {
        id: 'apps.appVersionIdTableCell.activeVersionSubtitle',
        defaultMessage: 'Active'
    },
    productionSlotName: {
        id: 'apps.appVersionIdTableCell.productionSlotName',
        defaultMessage: 'Production'
    },
    stagingSlotName: {
        id: 'apps.appVersionIdTableCell.stagingSlotName',
        defaultMessage: 'Staging'
    }
});

/**
 * Represents the cell that renders the application version id
 * along with the state specific message that applies to that
 * version id based on the published state.
 *
 * When a version is active, published to staging, or published to
 * production, we would like to convey that information to the user
 * and thus this cell renders the version along with that information.
 *
 * @param props The input to this cell.
 */
export const AppVersionIdTableCell = observer((props: AppVersionTableCellProps) => {
    const { version, versionStates, columnWidth } = props;
    const { localize } = useLocalization();

    /**
     * Gets the version state to display for the given version by
     * concatenating the states that apply. Examples can be:
     *
     * - Active
     * - Active & Staging
     * - Active & Production
     *
     * ... etc. based on the version publish state meta data.
     *
     * @param versionId The version to get the state string for.
     * @returns A string describing the state of the version.
     */
    const getVersionStateString = (): string => {
        if (!versionStates) {
            return '';
        }

        const stringParts: string[] = [];

        if (versionStates.activeVersionId === version.id) {
            stringParts.push(localize(messages.activeVersionSubtitle));
        }

        if (versionStates.stagingSlotMetaData && versionStates.stagingSlotMetaData.id === version.id) {
            stringParts.push(localize(messages.stagingSlotName));
        }

        if (versionStates.productionSlotMetaData && versionStates.productionSlotMetaData.id === version.id) {
            stringParts.push(localize(messages.productionSlotName));
        }

        return stringParts.join(' & ');
    };

    const stateString = getVersionStateString();

    return (
        <Stack verticalFill verticalAlign="center">
            <TruncatedText maxWidth={columnWidth}>
                {version ? (
                    <LuisText variant="smallPlus">
                        {version.id}
                        &nbsp;
                    </LuisText>
                ) : null}

                {stateString ? (
                    <LuisText variant="smallPlus" as="i" lineHeight="1.5">
                        ({stateString})
                    </LuisText>
                ) : null}
            </TruncatedText>
        </Stack>
    );
});
